import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiService, ApiResponse } from './api.service';
import { AccountCompanyService } from './account-company.service';
import {
  AccountCompanyWiseConfigDto,
  AccountCompanyWiseConfigSaveDto,
} from '../models/dtos/account-company-wise-config.dto';

@Injectable({
  providedIn: 'root',
})
export class AccountCompanyWiseConfigService {
  private readonly api = inject(ApiService);
  private readonly companyService = inject(AccountCompanyService);

  /** Latest loaded company-wise config (entry modes + F11 features). */
  readonly config = signal<AccountCompanyWiseConfigDto | null>(null);

  /** Clears cached config when the active company changes. */
  clearCache(): void {
    this.config.set(null);
  }

  /** Loads config for the session's active company (preferred over getDefault). */
  loadForActiveCompany(): Observable<ApiResponse<AccountCompanyWiseConfigDto>> {
    return this.getByCompanyId(this.companyService.getActiveCompanyId());
  }

  /** @deprecated Use loadForActiveCompany — default endpoint ignores session company. */
  getDefault(): Observable<ApiResponse<AccountCompanyWiseConfigDto>> {
    return this.loadForActiveCompany();
  }

  getByCompanyId(companyId: string): Observable<ApiResponse<AccountCompanyWiseConfigDto>> {
    const id = companyId.trim().toLowerCase();
    return this.api
      .get<AccountCompanyWiseConfigDto>(`AccountCompanyWiseConfig/by-company/${id}`)
      .pipe(
        tap((res) => {
          if (res.success && res.data) this.config.set(res.data);
        }),
      );
  }

  save(dto: AccountCompanyWiseConfigSaveDto): Observable<ApiResponse<boolean>> {
    return this.api.post<boolean>('AccountCompanyWiseConfig/save', dto).pipe(
        tap((res) => {
        if (res.success) {
          const prev = this.config();
          this.config.set({
            companyId: dto.companyId,
            singleDoubleMode: dto.singleDoubleMode,
            accountItemMode: dto.accountItemMode,
            openingBalanceDate: dto.openingBalanceDate ?? null,
            enableBatches: dto.enableBatches,
            maintainExpiryDatesForBatches: dto.maintainExpiryDatesForBatches,
            enablePaymentRequests: dto.enablePaymentRequests,
            paymentRequestUpiId: dto.paymentRequestUpiId ?? null,
            enableDiscount: dto.enableDiscount,
            enableActualAndBilledQuantity: dto.enableActualAndBilledQuantity,
            enableMultiPriceLevels: dto.enableMultiPriceLevels,
            commonLedgerForItemAllocation:
              dto.commonLedgerForItemAllocation ?? prev?.commonLedgerForItemAllocation !== false,
            showRunningBalance: dto.showRunningBalance ?? prev?.showRunningBalance ?? false,
            additionalDescriptionForLedger:
              dto.additionalDescriptionForLedger ?? prev?.additionalDescriptionForLedger ?? false,
            additionalDescriptionForStockItem:
              dto.additionalDescriptionForStockItem ?? prev?.additionalDescriptionForStockItem ?? false,
            showNarrations: dto.showNarrations ?? prev?.showNarrations ?? false,
          });
        }
      }),
    );
  }
}
